import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { Observable, forkJoin, throwError, timer, of, from } from 'rxjs';
import { finalize, map, mergeMap, retryWhen, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Config } from 'src/app/models/config';
import { AppService } from '../../app.service';
import { LoaderService, RequestLoaderDto } from '../loader.service';
import { SnackbarService } from "src/app/services/snackbar-service";
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

@Injectable({
    providedIn: 'root'
})

export class HttpBaseService {
    apiUrl: string;
    user: string;
    readonly retryAtempt = 0;

    constructor(
        private http: HttpClient, 
        private appService: AppService, 
        private loader: LoaderService,
        private snackbarService: SnackbarService,
        @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
    ) {
        this.apiUrl = environment.datadropApiUrl;
        this.user = Config.username;
    }

    getIdentity(): string {
        return this.appService.readConfig().userINumber + "@verisk.com";
    }

    // get headers with auth token - returns HttpHeaders
    private async getAuthHeaders(additionalHeaders: any = null): Promise<HttpHeaders> {
        const token = await this.oktaAuth.getAccessToken();
        const headersConfig = {
            'identity': this.getIdentity(),
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...additionalHeaders
        };
        return new HttpHeaders(headersConfig);
    }

    getAllPages<T>(relativeUrl: string, pageSize: number = environment.numberItemsInPage, params: HttpParams = null, headers: any = null): Observable<any> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const requestLoaderDto = new RequestLoaderDto(relativeUrl);
                this.loader.requestStart(requestLoaderDto);

                const createRequestForAllPages = (data: any) => {
                    this.loader.requestFinished(requestLoaderDto);
                    const requests = [];
                    requests.push(of(data));
                    for (let pageIndex = 2; pageIndex <= data.totalPages; pageIndex++) {
                        const request = this.http.get<T>(this.apiUrl + relativeUrl + this.getPageParam(pageIndex, pageSize), {
                            headers: authHeaders,
                            params: params,
                            responseType: 'json'
                        });
                        requests.push(request);
                    }

                    const requestFork = new RequestLoaderDto(relativeUrl + " +requestFork");
                    this.loader.requestStart(requestFork);

                    const joinAllDataFromForkJoin = (x: any) => {
                        if (x.length == 0)
                            return;
                        const acc = [];
                        for (const y of x) {
                            acc.push(...y.data);
                        }
                        this.loader.requestFinished(requestFork);
                        return {
                            data: acc,
                            pageSize: x[0].pageSize,
                            totalCount: x[0].totalCount,
                            totalPages: x[0].totalPages
                        };
                    };
                    return forkJoin(requests)
                        .pipe(
                            map(joinAllDataFromForkJoin),
                            retryWhen(
                                this.retryStategy(() => { this.loader.requestFinished(requestFork) })
                            ),
                            finalize(() => {
                                this.loader.requestFinished(requestLoaderDto);
                            })
                        );
                }

                const resRequest = this.http.get<T>(this.apiUrl + relativeUrl + this.getPageParam(1, pageSize), {
                    headers: authHeaders,
                    params: params,
                    responseType: 'json'
                }).pipe(
                    switchMap(createRequestForAllPages),
                    retryWhen(
                        this.retryStategy(() => {
                            this.loader.requestFinished(requestLoaderDto);
                        })
                    ),
                    finalize(() => {
                        this.loader.requestFinished(requestLoaderDto);
                    })
                );
                return resRequest;
            })
        );
    }

    getPageParam(pageNumber: number, pageSize: number) {
        return `?PageNumber=${pageNumber}&pageSize=${pageSize}`;
    }

    get<T>(relativeUrl: string, params: HttpParams = null, headers: any = null): Observable<T> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const request = this.http.get<T>(this.apiUrl + relativeUrl, {
                    headers: authHeaders,
                    params: params,
                    responseType: 'json'
                });
                return this.addBaseHttpStategy<T>(request, relativeUrl);
            })
        );
    }

    create<T>(relativeUrl: string, body: any, params: HttpParams = null, headers: any = null): Observable<T> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const request = this.http.post<T>(this.apiUrl + relativeUrl,
                    body,
                    {
                        headers: authHeaders,
                        params: params,
                        responseType: 'json'
                    });
                return this.addBaseHttpStategy<T>(request, relativeUrl);
            })
        );
    }

    update<T>(relativeUrl: string, body: any, params: HttpParams = null, headers: any = null): Observable<T> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const request = this.http.put<T>(this.apiUrl + relativeUrl,
                    body,
                    {
                        headers: authHeaders,
                        params: params,
                        responseType: 'json'
                    });
                return this.addBaseHttpStategy<T>(request, relativeUrl);
            })
        );
    }

    patch<T>(relativeUrl: string, body: any, params: HttpParams = null, headers: any = null): Observable<T> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const request = this.http.patch<T>(this.apiUrl + relativeUrl,
                    body,
                    {
                        headers: authHeaders,
                        params: params,
                        responseType: 'json'
                    });
                return this.addBaseHttpStategy<T>(request, relativeUrl);
            })
        );
    }

    delete<T>(relativeUrl: string, params: HttpParams = null, headers: any = null): Observable<T> {
        return from(this.getAuthHeaders(headers)).pipe(
            switchMap(authHeaders => {
                const request = this.http.delete<T>(this.apiUrl + relativeUrl,
                    {
                        headers: authHeaders,
                        params: params,
                        responseType: 'json'
                    });
                return this.addBaseHttpStategy<T>(request, relativeUrl);
            })
        );
    }

    runJob<T>(relativeUrl: string, body: any, params: HttpParams = null, headers: any = null): Observable<T> {
    return from(this.getAuthHeaders(headers)).pipe(
        switchMap(authHeaders => {
            const options: {
                headers?: HttpHeaders,
                observe?: 'body',
                params?: HttpParams,
                reportProgress?: boolean,
                responseType: 'text',
                withCredentials?: boolean
            } = {
                headers: authHeaders,
                params: params,
                responseType: 'text'
            };
            const request = this.http.post(this.apiUrl + relativeUrl,
                body,
                options
            );
            // ✅ Cast to Observable<any> first, then to Observable<T>
            return this.addBaseHttpStategy<any>(request as Observable<any>, relativeUrl) as Observable<T>;
        })
    );
}

    private addBaseHttpStategy<T>(request: Observable<T>, description: string): Observable<T> {
        const requestLoaderDto = new RequestLoaderDto(description);
        this.loader.requestStart(requestLoaderDto);
        return request.pipe(
            map((res: T) => {
                this.loader.requestFinished(requestLoaderDto);
                return res;
            }),
            retryWhen(
                this.retryStategy(() => { this.loader.requestFinished(requestLoaderDto) })
            ),
            finalize(() => {
                this.loader.requestFinished(requestLoaderDto);
            })
        );
    }

    private retryStategy = (cbFinis) => (attempts: Observable<any>) => {
        const deleyTable = [10, 15, 20];
        return attempts.pipe(
            mergeMap((error, index) => {
                const message = error.message ?? error;
                const status = error.status || this.extractStatusFromMessage(message);
                
                // ✅ Handle 401/403 authentication errors
                if (status === 401 || status === 403) {
                    const userFriendlyMessage = this.getUserFriendlyErrorMessage(error);
                    this.snackbarService.showError(userFriendlyMessage);
                    return throwError(() => error);
                }
                
                if (status === 400 || status === 404 || status === 409 || message.includes('400') || message.includes('404') || message.includes('409')) {
                    const userFriendlyMessage = this.getUserFriendlyErrorMessage(error);
                    this.snackbarService.showError(userFriendlyMessage);
                    return throwError(() => error);
                }
                if (index < deleyTable.length) {
                    return timer(deleyTable[index] * 1000);
                } else {
                    const userFriendlyMessage = this.getUserFriendlyErrorMessage(error);
                    this.snackbarService.showError(userFriendlyMessage);
                    return throwError(() => error);
                }
            }),
            finalize(() => {
                cbFinis();
            })
        );
    };

    private getUserFriendlyErrorMessage(error: any): string {
        const status = error.status || this.extractStatusFromMessage(error.message || error.toString());
        
        switch (status) {
            case 500:
                return 'Server error occurred. Please try again later.';
            case 409:
                return 'This item is in use and cannot be deleted.';
            case 404:
                return 'The requested item was not found. It may have been deleted.';
            case 403:
                return 'You do not have permission to perform this action.';
            case 401:
                return 'Authentication failed. Please log in again.';
            case 400:
                return 'The request failed due to a processing error.';
            default:
                return 'An unexpected error occurred. Please try again.';
        }
    }

    private extractStatusFromMessage(message: string): number | null {
        const statusRegex = /:\s*(\d{3})/;
        const statusMatch = statusRegex.exec(message);
        return statusMatch ? parseInt(statusMatch[1]) : null;
    }
}